import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import { UserContext } from "../../Context/UserContext";
import CurrencySymbol, { resolveCurrencyCode } from "../CurrencySymbol";
import "../Form/CreateCustomerPage.css";
const isElectron = false;
const ipcRenderer = null;
function Aggregator() {
  const [transactionNumber, setTransactionNumber] = useState("");
  const [errors, setErrors] = useState({});
  const [billDetails, setBillDetails] = useState(null);
  const [cardData, setCardData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, getHeaders } = useContext(UserContext);
  const inputRef = useRef(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("warning");
  const [pendingAction, setPendingAction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [settings, setSettings] = useState({});
  const shortcutKey = `isKeyboardShortcutsEnabled_${localStorage.getItem('active_company') || 'Company'}_${localStorage.getItem('active_branch') || 'Branch'}`;
  const isKeyboardShortcutsEnabled = localStorage.getItem(shortcutKey) === "true";
  // Current time state for real-time updates (like OpeningEntry)
  const [currentTime, setCurrentTime] = useState(new Date());
  const API_URL = '';
  // Default print settings (used when no active settings fetched)
  const defaultPrintSettings = {
    headerBlocks: [
      { id: 'h1', text: 'My Restaurant', align: 'center', label: 'Restaurant Name' },
      { id: 'h2', text: '123 Store Street, City', align: 'center', label: 'Street' },
      { id: 'h3', text: 'Phone: +91 123-456-7890', align: 'center', label: 'Phone' },
      { id: 'h4', text: 'GSTIN: 12ABCDE3456F7Z8', align: 'center', label: 'GSTIN' },
    ],
    footerBlocks: [
      { id: 'f1', text: 'Thank You! Visit Again!', align: 'center', label: 'Thank You Message' },
      { id: 'f2', text: 'Powered by MyRestaurant', align: 'center', label: 'Powered By' },
    ]
  };
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  // Fetch logo for preview
  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/logo`);
      if (response.data.logo) {
        setLogoUrl(API_URL + response.data.logo);
      }
    } catch (err) {
      console.error("Failed to fetch logo for preview:", err);
      setLogoUrl(null); // Ensure null if fetch fails
    }
  };
  // Fetch system settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`);
        if (response.data) {
          setSettings(response.data);
          localStorage.setItem('systemSettings', JSON.stringify(response.data));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        const stored = localStorage.getItem('systemSettings');
        if (stored) {
          setSettings(JSON.parse(stored));
        }
      }
    };
    fetchSettings();
  }, []);
  // Dynamic date formatting based on system settings (local time only)
  const getFormattedDate = (date, dateFormat) => {
    if (!dateFormat) {
      // Default: yyyy longmonth dd
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    const numericFormatter = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: 'numeric' });
    const parts = numericFormatter.formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    switch (dateFormat) {
      case 'dd-mm-yyyy':
        return `${day.padStart(2, '0')}-${month}-${year}`;
      case 'mm-dd-yyyy':
        return `${month}-${day.padStart(2, '0')}-${year}`;
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day.padStart(2, '0')}`;
      case 'dd/mm/yyyy':
        return `${day.padStart(2, '0')}/${month}/${year}`;
      case 'mm/dd/yyyy':
        return `${month}/${day.padStart(2, '0')}/${year}`;
      case 'yyyy/mm/dd':
        return `${year}/${month}/${day.padStart(2, '0')}`;
      case 'yyyy-long-mm-dd':
        // yyyy longmonth dd, e.g., 2025 October 29
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      case 'dd-MMM-yy':
        // dd-MMM-yy, e.g., 30-Oct-25
        const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
        const dayStr = date.getDate().toString().padStart(2, '0');
        const yearShort = date.getFullYear().toString().slice(-2);
        return `${dayStr}-${monthShort}-${yearShort}`;
      default:
        // Fallback to yyyy longmonth dd
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };
  // Dynamic time formatting based on system settings (local time only, conditional seconds) - Fixed to match OpeningEntry logic
  const getFormattedTime = (date, timeFormat) => {
    if (!timeFormat) {
      // Default: 12-hour without seconds
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    const hasSeconds = timeFormat.includes(':ss') || timeFormat.includes('ss');
    const is12Hour = timeFormat.includes(' a') || timeFormat.startsWith('hh');
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      ...(hasSeconds && { second: '2-digit' }),
      hour12: is12Hour,
    };
    return date.toLocaleTimeString('en-US', options);
  };
  // Currency formatter for totals - Ensures symbol placement based on currency (e.g., INR before number)
  const getCurrencyFormatter = (currencyOverride = null, precisionOverride = null, asHtml = false, asReact = false) => {
    // Resolve currency from active context using currency_map if not explicitly provided
    const activeComp = localStorage.getItem('active_company') || '';
    const activeBranch = localStorage.getItem('active_branch') || '';
    const currency = currencyOverride || resolveCurrencyCode(null, activeComp, activeBranch);
    const precision = precisionOverride !== null ? parseInt(precisionOverride) : (parseInt(settings.currencyPrecision) || 2);

    return {
      format: (value) => {
        const numValue = parseFloat(value) || 0;
        const fixedValue = numValue.toFixed(precision);
        if (asReact) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ marginRight: '2px', transform: 'translateY(1px)' }}>
                <CurrencySymbol currencyCode={currency} size={14} />
              </span>
              {fixedValue}
            </span>
          );
        } else if (asHtml) {
          if (currency?.toUpperCase() === 'AED') {
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const timestamp = new Date().getTime();
            return `<img src="${origin}/assets/Dirham%20Currency%20Symbol%20-%20Black.svg?v=${timestamp}" alt="AED" style="height: 12px; width: auto; vertical-align: middle; margin-right: 2px;" /> ${fixedValue}`;
          } else {
            const symbols = {
              INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
              SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
              QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
              CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
            };
            return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
          }
        } else {
          const symbols = {
            INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", JPY: "¥", CNY: "¥",
            SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
            QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
            CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
          };
          return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
        }
      }
    };
  };

  // Dedicated formatters
  const formatCurrencyReact = (amount) => getCurrencyFormatter(null, 2, false, true).format(amount);
  const formatCurrencyHtml = (amount) => getCurrencyFormatter(null, 2, true, false).format(amount);
  const formatCurrencyString = (amount) => getCurrencyFormatter(null, null, false, false).format(amount);
  // Fetch active print settings
  useEffect(() => {
    const fetchPrintSettings = async () => {
      try {
        const response = await axios.get("/api/print_settings/active");
        setPrintSettings(response.data);
      } catch (err) {
        console.error("Failed to fetch active print settings:", err);
        // Use default if fetch fails
        setPrintSettings(defaultPrintSettings);
      }
    };
    fetchPrintSettings();
  }, []);
  // Fetch logo
  useEffect(() => {
    fetchLogo();
  }, []);
  // UPDATED: Helper to get VAT by rate (from FrontPage)
  const getVatByRate = (items) => {
    const byRate = {};
    items.forEach(item => {
      if (item.taxBreakdown) {
        Object.entries(item.taxBreakdown).forEach(([rate, amt]) => {
          byRate[rate] = (byRate[rate] || 0) + amt;
        });
      }
    });
    return byRate;
  };
  // UPDATED: Calculate tax total (from FrontPage)
  const calculateTaxTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.taxTotal || 0), 0);
  };
  // UPDATED: Calculate subtotal excl (from FrontPage)
  const calculateExclTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.exclTotal || 0), 0);
  };
  // Initialize bill details from location state or use hardcoded data
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (location.state?.billDetails) {
      const defaultTime = currentTime.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      const items = location.state.billDetails.items || [];
      const formattedBillDetails = {
        ...location.state.billDetails,
        invoice_no: location.state.billDetails.invoice_no || `INV-${Date.now()}`,
        subtotal: Number(location.state.billDetails.subtotal) || calculateExclTotal(items),
        vat_amount: Number(location.state.billDetails.vat_amount) || calculateTaxTotal(items),
        totalAmount: Number(location.state.billDetails.totalAmount) || 0,
        customerName: location.state.billDetails.customerName || location.state.billDetails.customer || "N/A",
        phoneNumber: location.state.billDetails.phoneNumber || location.state.billDetails.phone || "N/A",
        email: location.state.billDetails.email || "N/A",
        whatsappNumber: location.state.billDetails.whatsappNumber || "N/A",
        tableNumber: location.state.billDetails.tableNumber || "N/A",
        deliveryAddress: location.state.billDetails.deliveryAddress || {
          building_name: "",
          flat_villa_no: "",
          country: "",
          field1: "",
          field2: "",
          field3: "",
        },
        date: location.state.billDetails.date || currentTime.toISOString().split("T")[0],
        time: location.state.billDetails.time || defaultTime,
        payments: location.state.billDetails.payments || [{ mode_of_payment: location.state?.method || "AGGREGATOR" }],
        items: items.map((item) => ({
          ...item,
          item_name: item.item_name || item.name || "Unnamed Item",
          quantity: Number(item.quantity) || 1,
          basePrice: Number(item.basePrice) || 0,
          originalBasePrice: item.originalBasePrice || null,
          totalPrice: Number(item.totalPrice) || 0,
          exclTotal: Number(item.exclTotal) || 0,
          taxTotal: Number(item.taxTotal) || 0,
          taxBreakdown: item.taxBreakdown || {},
          selectedSize: item.selectedSize || null,
          icePreference: item.icePreference || "without_ice",
          icePrice: Number(item.icePrice) || 0,
          isSpicy: item.isSpicy || false,
          spicyPrice: Number(item.spicyPrice) || 0,
          addonQuantities: item.addonQuantities || {},
          addonVariants: item.addonVariants || {},
          addonPrices: item.addonPrices || {},
          addonSizePrices: item.addonSizePrices || {},
          addonSpicyPrices: item.addonSpicyPrices || {},
          addonImages: item.addonImages || {},
          comboQuantities: item.comboQuantities || {},
          comboVariants: item.comboVariants || {},
          comboPrices: item.comboPrices || {},
          comboSizePrices: item.comboSizePrices || {},
          comboSpicyPrices: item.comboSpicyPrices || {},
          comboImages: item.comboImages || {},
          selectedCombos: item.selectedCombos || [],
          kitchen: item.kitchen || "Main Kitchen",
          ingredients: item.ingredients || [],
          selectedCustomVariants: item.selectedCustomVariants || {},
          customVariantsDetails: item.customVariantsDetails || {},
          customVariantsQuantities: item.customVariantsQuantities || {},
          itemToppings: item.itemToppings || {},
          addons: item.addons || [],
          combos: item.selectedCombos || [],
          isCombo: item.isCombo || false,
          comboItems: item.comboItems || [],
        })),
        vatByRate: getVatByRate(items),
      };
      setBillDetails(formattedBillDetails);
      setEmailAddress(formattedBillDetails.email);
    } else {
      const hardcodedBillDetails = {
        invoice_no: `INV-${Date.now()}`,
        customerName: "Manoj",
        phoneNumber: "+918921083090",
        email: "manoj.k88680@gmail.com",
        whatsappNumber: "+918921083090",
        tableNumber: "N/A",
        deliveryAddress: {
          building_name: "23rw",
          flat_villa_no: "1123",
          country: "India",
          field1: "Street 1",
          field2: "Area",
          field3: "City",
        },
        date: currentTime.toISOString().split("T")[0],
        time: currentTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
        payments: [{ mode_of_payment: location.state?.method || "AGGREGATOR" }],
        subtotal: 310.00,
        vat_amount: 46.50,
        totalAmount: 356.50,
        vatByRate: { 15: 46.50 },
        items: [
          {
            item_name: "rice",
            basePrice: 300.00,
            exclTotal: 300.00,
            taxTotal: 45.00,
            taxBreakdown: { 15: 45.00 },
            quantity: 1,
            selectedSize: null,
            icePreference: "without_ice",
            icePrice: 0,
            isSpicy: false,
            spicyPrice: 0,
            originalBasePrice: null,
            kitchen: "Main Kitchen",
            addons: [
              {
                name1: "ice",
                addon_quantity: 1,
                addon_price: 10.00,
                addon_total_price: 10.00,
                size: "M",
                isSpicy: false,
                spicyPrice: 0,
                kitchen: "Main Kitchen",
                tax_amount: 1.50,
              },
            ],
            combos: [],
            customVariantsDetails: {},
            customVariantsQuantities: {},
            isCombo: false,
            comboItems: [],
          },
        ],
      };
      setBillDetails(hardcodedBillDetails);
      setEmailAddress(hardcodedBillDetails.email);
    }
  }, [location, currentTime]);
  // Auto-close modal and navigate after 100 seconds
  useEffect(() => {
    let timer;
    if (showModal) {
      timer = setTimeout(() => {
        setShowModal(false);
        navigate("/frontpage");
      }, 100000);
    }
    return () => clearTimeout(timer);
  }, [showModal, navigate]);
  // Focus input when billDetails is loaded
  useEffect(() => {
    if (billDetails && inputRef.current) {
      inputRef.current.focus();
    }
  }, [billDetails]);
  // Calculate item prices including addons, combos, custom variants, and extras
  const calculateItemPrices = (item) => {
    if (item.isCombo) {
      return {
        basePrice: Number(item.basePrice) || 0,
        icePrice: 0,
        spicyPrice: 0,
        addonTotal: 0,
        comboTotal: 0,
        customVariantsTotal: 0,
        itemToppingsTotal: 0,
        totalAmount: (Number(item.basePrice) || 0) * item.quantity,
      };
    }
    const basePrice = Number(item.basePrice) || 0;
    const icePrice = item.icePreference === "with_ice" ? Number(item.icePrice) || 0 : 0;
    const spicyPrice = item.isSpicy ? Number(item.spicyPrice) || 0 : 0;
    const addonTotal =
      item.addons && item.addons.length > 0
        ? item.addons.reduce(
          (sum, addon) => sum + Number(addon.addon_total_price) * addon.addon_quantity,
          0
        )
        : 0;
    const comboTotal =
      item.combos && item.combos.length > 0
        ? item.combos.reduce(
          (sum, combo) => sum + Number(combo.combo_total_price) * combo.combo_quantity,
          0
        )
        : 0;
    const customVariantsTotal = item.customVariantsDetails
      ? Object.values(item.customVariantsDetails).reduce(
        (sum, variant) => sum + (Number(variant.price) || 0) * (item.customVariantsQuantities?.[variant.name] || 1),
        0
      ) * item.quantity
      : 0;
    const itemToppingsTotal = item.itemToppings
      ? Object.values(item.itemToppings).reduce(
        (sum, t) => sum + (Number(t.price) || 0) * (Number(t.qty) || 1),
        0
      )
      : 0;
    const totalAmount = (basePrice + icePrice + spicyPrice + customVariantsTotal + itemToppingsTotal) * item.quantity + addonTotal + comboTotal;
    return { basePrice, icePrice, spicyPrice, addonTotal, comboTotal, customVariantsTotal, itemToppingsTotal, totalAmount };
  };
  // Get display name for items
  const getItemDisplayName = (item) => {
    const sizeDisplay = item.selectedSize ? ` (${item.selectedSize})` : "";
    return `${item.item_name}${sizeDisplay}`;
  };
  // UPDATED: Use billDetails.subtotal for subtotal
  const calculateSubtotal = () => {
    return billDetails?.subtotal || 0;
  };
  // UPDATED: Use billDetails.vat_amount for VAT
  const calculateVAT = () => {
    return billDetails?.vat_amount || 0;
  };
  // UPDATED: Use billDetails.totalAmount for grand total
  const calculateGrandTotal = () => {
    return billDetails?.totalAmount || 0;
  };
  // UPDATED: Get VAT by rate from billDetails
  const getVatByRateFromBill = () => {
    return billDetails?.vatByRate || {};
  };
  // Parse bill date and time to Date object
  const parseBillDateTime = (dateStr, timeStr, timeZone) => {
    // Assume dateStr is 'YYYY-MM-DD', timeStr is 'HH:mm' or similar
    // Ensure timeStr is in HH:mm format (24-hour)
    let cleanTimeStr = timeStr;
    if (timeStr.includes(':')) {
      // If it has AM/PM, parse to 24-hour (simple check, assuming standard format)
      const [timePart, period] = timeStr.split(' ');
      if (period && (period.toUpperCase() === 'AM' || period.toUpperCase() === 'PM')) {
        let [hours, minutes] = timePart.split(':');
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (period.toUpperCase() === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
          hours = 0;
        }
        cleanTimeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    const fullDateStr = `${dateStr}T${cleanTimeStr}:00.000Z`; // Add Z for UTC
    const date = new Date(fullDateStr);
    if (isNaN(date.getTime())) {
      // Fallback to current date/time
      return new Date();
    }
    return date;
  };
  // Parse card swipe data
  const parseCardData = (swipeData) => {
    const track1Match = swipeData.match(/%B(\d{16})\^([^/]+)\/(.+)\^(\d{4})/);
    if (track1Match) {
      return {
        cardNumber: track1Match[1],
        lastName: track1Match[2],
        firstName: track1Match[3],
        expiry: track1Match[4],
      };
    }
    return null;
  };
  // Handle warning modal OK button
  const handleWarningOk = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setWarningMessage("");
    setWarningType("warning");
  };
  // Handle transaction number input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setTransactionNumber(value);
    if (value.startsWith("%B") && value.endsWith("?")) {
      const parsedData = parseCardData(value);
      if (parsedData) {
        setCardData(parsedData);
        setTransactionNumber(parsedData.cardNumber);
        processPayment(parsedData);
      } else {
        setErrors({ transactionNumber: "Invalid card swipe data." });
      }
    } else {
      setErrors({});
    }
  };
  const saveSaleIfRequired = async () => {
    let finalInvoiceNo = billDetails?.invoice_no;
    const { payload, frontPageState } = location.state || {};

    if (payload) {
      let response;
      if (finalInvoiceNo && !finalInvoiceNo.startsWith('INV-')) {
        const apiPath = baseUrl ? `${baseUrl}/api/sales/${finalInvoiceNo}` : `/api/sales/${finalInvoiceNo}`;
        response = await axios.put(apiPath, payload, { headers: getHeaders() });
      } else {
        const apiPath = baseUrl ? `${baseUrl}/api/sales` : '/api/sales';
        response = await axios.post(apiPath, payload, { headers: getHeaders() });
        finalInvoiceNo = response.data.invoice_no;
        setBillDetails(prev => ({ ...prev, invoice_no: finalInvoiceNo }));
      }

      const orderId = frontPageState?.orderId;
      if (orderId) {
        try {
          const apiPath = baseUrl ? `${baseUrl}/api/activeorders/${orderId}` : `/api/activeorders/${orderId}`;
          const markedCartItems = frontPageState.cartItems.map(item => ({ ...item, isPaid: true }));
          await axios.put(apiPath, { paid: true, cartItems: markedCartItems }, { headers: getHeaders() });
        } catch (error) {
          console.error("Error updating order paid status:", error);
        }
      }

      // Clear payload from state so we don't save twice
      navigate(location.pathname, { state: { ...location.state, payload: null }, replace: true });
    }
    return finalInvoiceNo;
  };

  const processPayment = async (cardData) => {
    setIsLoading(true);
    setError(null);
    console.log("Processing payment with card:", cardData);
    console.log("Amount:", calculateGrandTotal().toFixed(2));
    try {
      await saveSaleIfRequired();
      setIsLoading(false);
      setShowModal(true);
    } catch (error) {
      setIsLoading(false);
      setWarningMessage(`Failed to save sale: ${error.message}`);
      setWarningType("warning");
    }
  };
  // Simulate Scanner for testing
  const simulateCardSwipe = () => {
    const fakeSwipeData = "%B1234567890123456^DOE/JOHN^25051011000?";
    setTransactionNumber(fakeSwipeData);
    const parsedData = parseCardData(fakeSwipeData);
    if (parsedData) {
      setCardData(parsedData);
      setTransactionNumber(parsedData.cardNumber);
      processPayment(parsedData);
    }
  };
  // Validate transaction number
  const validateFields = () => {
    let newErrors = {};
    if (!transactionNumber || !/^[a-zA-Z0-9]{6,20}$/.test(transactionNumber)) {
      newErrors.transactionNumber = "Enter a valid transaction number (6-20 characters).";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (validateFields()) {
      if (cardData) {
        processPayment(cardData);
      } else {
        setIsLoading(true);
        setError(null);
        console.log("Manual payment with transaction number:", transactionNumber);
        try {
          await saveSaleIfRequired();
          setIsLoading(false);
          setShowModal(true);
        } catch (error) {
          setIsLoading(false);
          setWarningMessage(`Failed to save sale: ${error.message}`);
          setWarningType("warning");
        }
      }
    }
  };
  // Navigate back to main page
  const handleBack = () => {
    navigate("/frontpage");
  };

  // Edit order (return to frontpage with existing details)
  const handleEdit = () => {
    if (location.state?.frontPageState) {
      navigate("/frontpage", { state: location.state.frontPageState });
    } else if (billDetails) {
      navigate("/frontpage", { state: { existingOrder: billDetails, invoice_no: billDetails.invoice_no } });
    } else {
      navigate("/frontpage");
    }
  };
  // Format numbers for display using currency formatter
  const formatCurrency = (amount) => {
    return formatCurrencyReact(Number(amount));
  };
  // Helper to format delivery address in the specified order (single line for UI)
  const formatDeliveryAddress = (deliveryAddress) => {
    if (!deliveryAddress) return null;
    const parts = [
      deliveryAddress.flat_villa_no || "",
      deliveryAddress.building_name || "",
      deliveryAddress.field3 || "",
      deliveryAddress.field2 || "",
      deliveryAddress.field1 || "",
      deliveryAddress.country || ""
    ].filter(part => part.trim() !== ""); // Filter out empty parts
    return parts.length > 0 ? parts.join(", ") : null;
  };
  // Helper to format delivery address for print (multi-line HTML)
  const getPrintDeliveryAddressHtml = (deliveryAddress) => {
    if (!deliveryAddress) return null;
    const lines = [];
    // Line 1: flat_villa_no + building_name
    const line1 = [deliveryAddress.flat_villa_no, deliveryAddress.building_name].filter(Boolean).join(', ');
    if (line1) lines.push(line1);
    // Line 2: field3 + field2
    const line2 = [deliveryAddress.field3, deliveryAddress.field2].filter(Boolean).join(', ');
    if (line2) lines.push(line2);
    // Line 3: field1 + country
    const line3 = [deliveryAddress.field1, deliveryAddress.country].filter(Boolean).join(', ');
    if (line3) lines.push(line3);
    return lines.length > 0 ? lines.join('<br>') : null;
  };
  // Check if delivery address is available
  const hasDeliveryAddress = billDetails?.deliveryAddress &&
    (billDetails.deliveryAddress.building_name ||
      billDetails.deliveryAddress.flat_villa_no ||
      billDetails.deliveryAddress.country ||
      billDetails.deliveryAddress.field1 ||
      billDetails.deliveryAddress.field2 ||
      billDetails.deliveryAddress.field3);
  const formattedDeliveryAddress = formatDeliveryAddress(billDetails?.deliveryAddress);
  // UPDATED: Generate printable receipt content - with VAT breakdown
  const generatePrintableContent = (invoiceNoOverride = null, isPreview = false) => {
    if (!billDetails) return "";
    const subtotal = calculateSubtotal();
    const vatAmount = calculateVAT();
    const grandTotal = calculateGrandTotal();
    const vatByRate = getVatByRateFromBill();
    // Use currentTime for date and time in receipt (real-time like OpeningEntry)
    const formattedDate = getFormattedDate(currentTime, settings.dateFormat);
    const formattedTime = getFormattedTime(currentTime, settings.timeFormat);
    const deliveryAddressHtml = getPrintDeliveryAddressHtml(billDetails.deliveryAddress);
    const borderStyle = isPreview ? "border: none;" : "border: 1px solid #000000;";
    const effectivePrintSettings = printSettings || defaultPrintSettings;
    const headerHtml = (effectivePrintSettings.headerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const footerHtml = (effectivePrintSettings.footerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const formatter = { format: formatCurrencyHtml }; // Use updated formatter for consistent currency display
    const cardDetailsDisplay = cardData
      ? `
          <tr>
            <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Card Number</td>
            <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
            <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">**** **** **** ${cardData.cardNumber.slice(-4)}</td>
          </tr>
          <tr>
            <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Transaction Number</td>
            <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
            <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${transactionNumber}</td>
          </tr>
        `
      : `
          <tr>
            <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Transaction Number</td>
            <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
            <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${transactionNumber}</td>
          </tr>
        `;
    const offerRows = billDetails.items.filter(item => item.originalBasePrice).map(item => {
      return `
        <tr>
          <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${item.item_name}:</td>
          <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;"><span style="text-decoration: line-through;">${formatter.format(item.originalBasePrice * item.quantity)}</span> ${formatter.format(item.basePrice * item.quantity)}</td>
        </tr>
      `;
    }).join('');
    const vatBreakdownRows = Object.entries(vatByRate).map(([rate, amt]) => `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">VAT ${rate}%:</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(amt)}</td>
      </tr>
    `).join('');
    return `
      <div style="font-family: Arial, sans-serif; width: 88mm; margin: 0 auto; font-size: 12px; padding: 10px; color: #000000; ${borderStyle} box-sizing: border-box; line-height: 1.2;">
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 15px;">
          ${logoUrl ? `<div style="margin-bottom: 5px;"><img src="${logoUrl}" alt="Logo" style="max-width: 50px; max-height: 50px; object-fit: contain; border-radius: 3px;"/></div>` : ''}
          <div style="font-family: Arial, sans-serif; font-size: 12px; width: 100%;">
            ${headerHtml}
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="width: 50%; text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Invoice No</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="width: 50%; text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${invoiceNoOverride || billDetails.invoice_no || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Customer</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.customerName || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Phone</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.phoneNumber || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Email</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.email || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">WhatsApp</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.whatsappNumber || "N/A"}</td>
            </tr>
            ${billDetails.tableNumber && billDetails.tableNumber !== "N/A"
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Table</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.tableNumber}</td>
                  </tr>
                `
        : ""
      }
            ${hasDeliveryAddress && deliveryAddressHtml
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; vertical-align: top;">Delivery Address</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: pre-line; word-break: break-all;">${deliveryAddressHtml}</td>
                  </tr>
                `
        : ""
      }
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Payment Mode</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.payments?.[0]?.mode_of_payment || location.state?.method || "AGGREGATOR"}</td>
            </tr>
            ${cardDetailsDisplay}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Date</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedDate}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Time</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedTime}</td>
            </tr>
          </tbody>
        </table>
        <table style="width: 100%; margin-bottom: 10px; border-collapse: collapse; border: 1px solid #000000; table-layout: fixed;">
          <thead>
            <tr style="border-bottom: 1px dashed #000000;">
              <th style="text-align: left; width: 40%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Item</th>
              <th style="text-align: center; width: 15%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Qty</th>
              <th style="text-align: right; width: 20%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Price</th>
              <th style="text-align: right; width: 25%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${billDetails.items
        .map((item) => {
          const { basePrice, icePrice, spicyPrice, addonTotal, comboTotal } = calculateItemPrices(item);
          return `
                  <tr>
                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px; vertical-align: top;">${getItemDisplayName(item)}</td>
                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${item.quantity}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice)}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice * item.quantity)}</td>
                  </tr>
                  ${item.isCombo && item.comboItems && item.comboItems.length > 0
              ? item.comboItems
                .map(
                  (comboItem) => `
                              <tr>
                                <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${comboItem.name}</td>
                                <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(comboItem.price)}</td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(comboItem.price * item.quantity)}</td>
                              </tr>
                            `
                )
                .join("")
              : ""
            }
                  ${item.icePreference === "with_ice" && icePrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Ice</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.isSpicy && spicyPrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Spicy</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0
              ? Object.entries(item.customVariantsDetails)
                .map(
                  ([variantName, variant]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${variant.heading}: ${variant.name}</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.customVariantsQuantities?.[variantName] || 1}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price * (item.customVariantsQuantities?.[variantName] || 1))}</td>
                            </tr>
                          `
                )
                .join("")
              : ""
            }
                  ${item.itemToppings && Object.keys(item.itemToppings).length > 0
              ? Object.entries(item.itemToppings)
                .map(
                  ([name, top]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity * (top.qty || 1)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * (item.quantity * (top.qty || 1)))}</td>
                            </tr>
                          `
                )
                .join("")
              : ""
            }
                  ${item.addons && item.addons.length > 0
              ? item.addons
                .map(
                  (addon) =>
                    addon.addon_quantity > 0
                      ? `
                                  <tr>
                                    <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Addon: ${addon.addon_name}${addon.size ? ` (${addon.size})` : ""}</td>
                                    <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${addon.addon_quantity}</td>
                                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(addon.addon_price)}</td>
                                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(addon.addon_price * addon.addon_quantity)}</td>
                                  </tr>
                                  ${addon.isSpicy && addon.spicyPrice > 0
                        ? `
                                        <tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #999; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${addon.addon_quantity}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(addon.spicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(addon.spicyPrice * addon.addon_quantity)}</td>
                                        </tr>
                                      `
                        : ""
                      }
                                `
                      : ""
                )
                .join("")
              : ""
            }
                  ${item.combos && item.combos.length > 0
              ? item.combos
                .map(
                  (combo) =>
                    combo.combo_quantity > 0
                      ? `
                                  <tr>
                                    <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Combo: ${combo.name1}${combo.size ? ` (${combo.size})` : ""}</td>
                                    <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${combo.combo_quantity}</td>
                                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(combo.combo_price)}</td>
                                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(combo.combo_price * combo.combo_quantity)}</td>
                                  </tr>
                                  ${combo.isSpicy && combo.spicyPrice > 0
                        ? `
                                        <tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #999; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${combo.combo_quantity}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(combo.spicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(combo.spicyPrice * combo.combo_quantity)}</td>
                                        </tr>
                                      `
                        : ""
                      }
                                `
                      : ""
                )
                .join("")
              : ""
            }
                `;
        })
        .join("")}
          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total Quantity:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${billDetails.items.reduce((sum, item) => sum + item.quantity, 0)}</td>
            </tr>
            ${offerRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">Subtotal:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(subtotal)}</td>
            </tr>
            ${vatBreakdownRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total VAT:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(vatAmount)}</td>
            </tr>
            <tr style="font-weight: bold; border-top: 2px solid #000;">
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">Grand Total:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">${formatter.format(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top: 15px; width: 100%;">
          ${footerHtml}
        </div>
      </div>
    `;
  };
  // Handle print functionality
  const handlePrint = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const content = generatePrintableContent(billDetails?.invoice_no);

      if (isElectron && ipcRenderer) {
        ipcRenderer.send("open-print-preview", content);
        ipcRenderer.once("print-preview-response", (event, response) => {
          setIsLoading(false);
          if (response.success) {
            setShowModal(false);
            navigate("/frontpage");
          } else {
            setError("Failed to open print preview: " + response.error);
            setWarningMessage("Print preview failed: " + response.error);
            setWarningType("warning");
          }
        });
      } else {
        const win = window.open("", "_blank");
        win.document.write(`
          <html>
            <head>
              <title>Receipt - Invoice ${billDetails?.invoice_no || "N/A"}</title>
              <style>
                @media print {
                  body { margin: 0; }
                  @page { margin: 0; size: 88mm auto; }
                }
                body { margin: 0; }
              </style>
            </head>
            <body>
              ${content}
            </body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
        setIsLoading(false);
        setShowModal(false);
        navigate("/frontpage");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Printing failed: " + err.message);
      setWarningMessage("Printing failed: " + err.message);
      setWarningType("warning");
    }
  };
  // Handle email functionality
  const handleEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      setWarningMessage("Please enter a valid email address!");
      setWarningType("warning");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let htmlContent = generatePrintableContent(billDetails?.invoice_no);
      const publicUrl = "http://157.173.96.252:6034";
      if (htmlContent) {
        htmlContent = htmlContent.replaceAll('src="/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/api/images/', `src="${publicUrl}/api/images/`);
      }
      let emailWrapperHtml = "";
      if (htmlContent) {
        htmlContent = htmlContent.replace(/<img[^>]*Dirham%20Currency%20Symbol%20-%20Black\.svg[^>]*>/gi, '<span style="font-size: 11px; font-weight: bold; margin-right: 2px;">AED</span>');
        const customerName = billDetails?.customerName && billDetails.customerName !== 'N/A' ? billDetails.customerName : 'Customer';
        const activeBranch = localStorage.getItem('active_branch');
        const branchName = (activeBranch && activeBranch !== 'All Branches') ? activeBranch : null;
        const restName = settings?.restaurantName || branchName || localStorage.getItem('active_company') || 'Our Restaurant';
        const finalLogoUrl = logoUrl ? logoUrl.replaceAll('http://localhost:6034', publicUrl).replaceAll('http://localhost:3000', publicUrl).replaceAll('src="/static', `src="${publicUrl}/static`) : '';

        let emailSettingsEmail = settings?.email || 'Email not provided';
        try {
          const esRes = await axios.get('/api/get-email-settings', {
            headers: {
              'X-Company-Name': localStorage.getItem('active_company') || '',
              'X-Branch-Name': localStorage.getItem('active_branch') || ''
            }
          });
          if (esRes.data && esRes.data.settings) {
            emailSettingsEmail = esRes.data.settings.admin_email || esRes.data.settings.sender_email || emailSettingsEmail;
          }
        } catch (esErr) {
          console.error("Failed to fetch email settings", esErr);
        }

        const effectivePrintSettings = printSettings || defaultPrintSettings;
        const phoneBlock = (effectivePrintSettings.headerBlocks || []).find(b =>
          (b.label && b.label.toLowerCase().includes('phone')) ||
          (b.text && (b.text.includes('+') || /\d{9,}/.test(b.text.replace(/\s|-/g, ''))))
        );
        const displayPhone = phoneBlock ? phoneBlock.text : (settings?.phone || 'Phone not provided');

        emailWrapperHtml = `
<div style="background-color: #f4f6f8; padding: 40px 20px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.05);">
    
    <!-- 1. Restaurant Logo Section -->
    <div style="text-align: center; padding: 20px;">
      ${finalLogoUrl ? `<img src="${finalLogoUrl}" alt="Restaurant Logo" style="max-width: 120px; max-height: 80px;" />` : ''}
    </div>

    <!-- 2. Header Section (Purple Background) -->
    <div style="background-color: #604BE8; color: #ffffff; padding: 25px 20px; text-align: center;">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">🍽️ Thank You for Your Order!</h1>
    </div>

    <!-- 3. Personalized Greeting Section -->
    <div style="padding: 30px 30px 15px 30px; font-size: 16px; color: #333333; line-height: 1.6;">
      <p style="margin-top: 0;">Hi ${customerName},</p>
      
      <p><strong>Your order has been confirmed successfully.</strong></p>
      
      <p>Thank you for choosing <strong>${restName}</strong>. Your receipt is provided below for your reference.</p>
      
      <p>We hope you enjoyed your meal and had a great experience with us. If you have any questions about your order or need any assistance, we're always happy to help.</p>
      
      <p>We look forward to serving you again soon.</p>
    </div>

    <!-- 4. The Invoice (Receipt) -->
    <div style="padding: 15px 30px 30px 30px; display: flex; justify-content: center;">
       ${htmlContent}
    </div>

    <!-- 5. Footer Section -->
    <div style="background-color: #f1f5f9; padding: 25px; text-align: left; border-top: 1px solid #e2e8f0; font-size: 15px; color: #475569;">
      <h3 style="margin-top: 0; color: #334155; font-size: 18px;">Need Assistance?</h3>
      <p style="margin: 5px 0;">📞 ${displayPhone}</p>
      <p style="margin: 5px 0;">📧 ${emailSettingsEmail}</p>
      <p style="margin: 5px 0;">🌐 <a href="${window.location.origin}" style="color: #604BE8; text-decoration: none;">${window.location.origin}</a></p>
      
      <p style="margin-top: 20px; font-weight: bold; color: #334155; text-align: center;">Thank you for being our valued customer.<br/>We look forward to serving you again soon! ❤️</p>
    </div>

  </div>
</div>`;
      }
      const emailContent = {
        to: emailAddress,
        subject: `Receipt from ${settings?.restaurantName || 'My Restaurant'} - ${billDetails?.invoice_no || "N/A"}`,
        html: emailWrapperHtml,
      };

      const response = await axios.post("/api/send-email", emailContent, {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      });
      setIsLoading(false);
      if (response.data.success) {
        setWarningMessage("Receipt sent successfully to " + emailAddress);
        setWarningType("success");
      } else {
        setError("Failed to send email: " + response.data.message);
        setWarningMessage("Failed to send email: " + response.data.message);
        setWarningType("warning");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningMessage("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningType("warning");
    }
  };
  // Handle cancel (only close modal, stay on page)
  const handleCancel = () => {
    setShowModal(false);
  };
  // Handle modal close with navigation to frontpage
  const handleModalClose = () => {
    setShowModal(false);
    navigate("/frontpage");
  };
  // Render receipt preview in UI (similar to printsettings) - Hidden in UI, shown only in print
  const renderReceiptPreview = () => {
    if (!billDetails) return null;
    const effectivePrintSettings = printSettings || defaultPrintSettings;
    // Use currentTime for preview
    const formattedDatePreview = getFormattedDate(currentTime, settings.dateFormat);
    const formattedTimePreview = getFormattedTime(currentTime, settings.timeFormat);
    // Render receipt preview
    return (
      <div style={{ display: 'none' }}> {/* Hidden in UI */}
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', textAlign: 'center' }}>Receipt Preview</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Logo on the left */}
          {logoUrl && (
            <div style={{ flex: '0 0 auto' }}>
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  width: '50px',
                  height: '50px',
                  objectFit: 'contain',
                  borderRadius: '5px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none'; // Hide if error
                }}
              />
            </div>
          )}
          {/* Restaurant details on the right */}
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', width: '100%' }}>
            {(effectivePrintSettings.headerBlocks || []).map((b, i) => (
              <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '10px', fontSize: '10px' }}>
          <p>Invoice: {billDetails.invoice_no}</p>
          <p>Customer: {billDetails.customerName}</p>
          <p>Date: {formattedDatePreview}</p>
          <p>Time: {formattedTimePreview}</p>
          <p>Total: {formatCurrency(calculateGrandTotal())}</p>
        </div>
        <div style={{ marginTop: '16px' }}>
          {(effectivePrintSettings.footerBlocks || []).map((b, i) => (
            <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
          ))}
        </div>
      </div>
    );
  };

  // Keyboard Shortcuts Effect
  useEffect(() => {
    if (!isKeyboardShortcutsEnabled) return;

    const handleKeyDown = (e) => {
      // Prevent browser defaults for F keys
      if (e.key === 'F2' || e.key === 'F4' || e.key === 'F8') {
        e.preventDefault();
      }

      if (e.key === 'Escape' && showModal) {
        e.preventDefault();
        handleCancel();
        return;
      }

      if (e.target.tagName === 'INPUT' && e.key !== 'Enter') return;
      if (e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'F4') {
        if (!showModal && billDetails) handleCardSubmit({ preventDefault: () => { } });
      } else if (e.key === 'F2') {
        if (!showModal) handleBack();
      } else if (e.key === 'F8') {
        if (showModal) {
          e.preventDefault();
          handleEmail();
        }
      } else if (e.key === 'Enter') {
        if (showModal) {
          e.preventDefault();
          handlePrint();
        } else if (e.target.tagName === 'INPUT' && e.target.id === 'transactionNumber') {
          e.preventDefault();
          handleCardSubmit({ preventDefault: () => { } });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardShortcutsEnabled, showModal, billDetails, transactionNumber, cardData]);

  return (
    <div className="create-customer-container main-customer-page">
      <style>{'.cash-card-override { width: 100% !important; max-width: 750px !important; margin-left: auto !important; margin-right: auto !important; }'}</style>
      {/* Header section matching CreateCustomerPage */}
      <div className="customer-header-section">
        <div className="header-left">
          <button className="header-back-btn" onClick={handleBack} disabled={isLoading}>
            <i className="fas fa-arrow-left"></i> Back to Main {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ marginLeft: '8px', fontSize: '0.8em', padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}>F2</span>}
          </button>
        </div>
        <div className="header-center">
          <h1>Aggregator Payment</h1>
        </div>
        <div className="header-right header-actions"></div>
      </div>

      <div className="customer-scroll-body">
        <div className="main-content-card">
          {/* Warning message display */}
          {warningMessage && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10050,
              animation: 'fadeIn 0.2s ease-out'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '30px',
                width: '420px',
                maxWidth: '90%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  color: warningType === 'success' ? '#10b981' : '#f59e0b'
                }}>
                  {warningType === 'success' ? '✓' : '⚠'}
                </div>
                <h4 style={{
                  margin: '0 0 12px 0',
                  color: '#1e293b',
                  fontWeight: '700',
                  fontSize: '18px'
                }}>
                  {warningType === 'success' ? 'Success' : 'Notification'}
                </h4>
                <p style={{
                  margin: '0 0 24px 0',
                  color: '#64748b',
                  fontSize: '15px',
                  lineHeight: '1.5'
                }}>
                  {warningMessage}
                </p>
                <button
                  onClick={handleWarningOk}
                  className="btn btn-primary"
                  style={{
                    padding: '12px 30px',
                    background: warningType === 'success' ? '#10b981' : '#604BE8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {/* Error message display */}
          {error && <div className="card-error">{error}</div>}

          <div className="payment-sections-wrapper" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <div style={{ width: '100%', maxWidth: '750px' }}>
              <div className="form-section-card theme-blue cash-card cash-card-override" style={{ width: '100%', margin: '0 auto' }}>
                <div className="cash-body">
                  {billDetails ? (
                    <div>
                      {/* Customer information */}
                      <div className="cash-customer-info">
                        <p>
                          <strong>Customer:</strong> {billDetails.customerName}
                        </p>
                        <p>
                          <strong>Phone:</strong> {billDetails.phoneNumber}
                        </p>
                        <p>
                          <strong>Email:</strong> {billDetails.email}
                        </p>
                        {billDetails.tableNumber && billDetails.tableNumber !== "N/A" && (
                          <p>
                            <strong>Table:</strong> {billDetails.tableNumber}
                          </p>
                        )}
                        {hasDeliveryAddress && formattedDeliveryAddress && (
                          <p>
                            <strong>Delivery Address:</strong> {formattedDeliveryAddress}
                          </p>
                        )}
                      </div>
                      <div className="section-header mt-4">
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#604BE8' }}>Items Ordered :</h2>
                      </div>
                      <div className="cash-table-wrapper table-responsive">
                        <table
                          className="table table-hover text-start"
                          style={{ fontSize: "14px", color: "var(--text-main)" }}
                        >
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}>T.No.</th>
                              <th>Item Details</th>
                              <th style={{ width: "80px" }}>Qty</th>
                              <th style={{ width: "80px" }}>Price</th>
                            </tr>
                          </thead>
                          <tbody className="text-start">
                            {billDetails.items.map((item, index) => {
                              const { basePrice, icePrice, spicyPrice } = calculateItemPrices(item);
                              return (
                                <React.Fragment key={index}>
                                  <tr>
                                    <td>{billDetails.tableNumber}</td>
                                    <td>
                                      <strong>{getItemDisplayName(item)}</strong>
                                    </td>
                                    <td>{item.quantity}</td>
                                    <td>
                                      {item.originalBasePrice ? (
                                        <>
                                          <span className="strikethroughStyle">{formatCurrency(item.originalBasePrice)}</span> {formatCurrency(basePrice)}
                                        </>
                                      ) : (
                                        formatCurrency(basePrice)
                                      )}
                                    </td>
                                  </tr>
                                  {item.isCombo && item.comboItems && item.comboItems.map((comboItem, cIndex) => (
                                    <tr className="cash-sub-item" key={`${index}-combo-${cIndex}`}>
                                      <td></td>
                                      <td>
                                        <div style={{ fontSize: "12px" }}>+ {comboItem.name}</div>
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{formatCurrency(comboItem.price)}</td>
                                    </tr>
                                  ))}
                                  {item.icePreference === "with_ice" && icePrice > 0 && (
                                    <tr className="cash-sub-item">
                                      <td></td>
                                      <td>
                                        <div style={{ fontSize: "12px" }}>+ Ice ({formatCurrency(icePrice)})</div>
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{formatCurrency(icePrice)}</td>
                                    </tr>
                                  )}
                                  {item.isSpicy && spicyPrice > 0 && (
                                    <tr className="cash-sub-item">
                                      <td></td>
                                      <td>
                                        <div style={{ fontSize: "12px" }}>+ Spicy ({formatCurrency(spicyPrice)})</div>
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{formatCurrency(spicyPrice)}</td>
                                    </tr>
                                  )}
                                  {item.customVariantsDetails &&
                                    Object.keys(item.customVariantsDetails).length > 0 &&
                                    Object.entries(item.customVariantsDetails).map(([variantName, variant], idx) => (
                                      <tr className="cash-sub-item" key={`${index}-custom-${idx}`}>
                                        <td></td>
                                        <td>
                                          <div style={{ fontSize: "12px" }}>
                                            + {variant.heading}: {variant.name} ({formatCurrency(variant.price)})
                                          </div>
                                        </td>
                                        <td>{item.customVariantsQuantities?.[variantName] || 1}</td>
                                        <td>{formatCurrency(variant.price)}</td>
                                      </tr>
                                    ))}
                                  {item.itemToppings &&
                                    Object.keys(item.itemToppings).length > 0 &&
                                    Object.entries(item.itemToppings).map(([name, top], idx) => (
                                      <tr className="cash-sub-item" key={`${index}-topping-${idx}`}>
                                        <td></td>
                                        <td>
                                          <div style={{ fontSize: "12px" }}>+ {top.name} (Topping)</div>
                                        </td>
                                        <td>{item.quantity * (top.qty || 1)}</td>
                                        <td>{formatCurrency(top.price)}</td>
                                      </tr>
                                    ))}
                                  {item.addons &&
                                    item.addons.map(
                                      (addon, idx) =>
                                        addon.addon_quantity > 0 && (
                                          <React.Fragment key={`${index}-addon-${idx}`}>
                                            <tr className="cash-sub-item">
                                              <td></td>
                                              <td>
                                                <div style={{ fontSize: "12px" }}>
                                                  + Addon: {addon.addon_name}
                                                  {addon.size ? ` (${addon.size})` : ""}
                                                </div>
                                              </td>
                                              <td>{addon.addon_quantity}</td>
                                              <td>{formatCurrency(addon.addon_price)}</td>
                                            </tr>
                                            {addon.isSpicy && addon.spicyPrice > 0 && (
                                              <tr className="cash-sub-item">
                                                <td></td>
                                                <td>
                                                  <div style={{ fontSize: "12px" }}>+ Spicy ({formatCurrency(addon.spicyPrice)})</div>
                                                </td>
                                                <td>{addon.addon_quantity}</td>
                                                <td>{formatCurrency(addon.spicyPrice)}</td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        )
                                    )}
                                  {item.combos &&
                                    item.combos.map(
                                      (combo, idx) =>
                                        combo.combo_quantity > 0 && (
                                          <React.Fragment key={`${index}-combo-${idx}`}>
                                            <tr className="cash-sub-item">
                                              <td></td>
                                              <td>
                                                <div style={{ fontSize: "12px" }}>
                                                  + Combo: {combo.name1}
                                                  {combo.size ? ` (${combo.size})` : ""}
                                                </div>
                                              </td>
                                              <td>{combo.combo_quantity}</td>
                                              <td>{formatCurrency(combo.combo_price)}</td>
                                            </tr>
                                            {combo.isSpicy && combo.spicyPrice > 0 && (
                                              <tr className="cash-sub-item">
                                                <td></td>
                                                <td>
                                                  <div style={{ fontSize: "12px" }}>+ Spicy ({formatCurrency(combo.spicyPrice)})</div>
                                                </td>
                                                <td>{combo.combo_quantity}</td>
                                                <td>{formatCurrency(combo.spicyPrice)}</td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        )
                                    )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="cash-totals" style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', marginTop: '24px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#334155', fontWeight: '700', fontSize: '15px' }}>
                          <span>Total Quantity:</span>
                          <span>{billDetails.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                        </div>
                        {billDetails.items.filter(item => item.originalBasePrice).map(item => (
                          <div key={item.item_name} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#334155', fontWeight: '600' }}>
                            <span>{item.item_name}:</span>
                            <span><span className="strikethroughStyle" style={{ marginRight: '8px', color: '#94a3b8' }}>{formatCurrency(item.originalBasePrice * item.quantity)}</span> {formatCurrency(item.basePrice * item.quantity)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#334155', fontWeight: '700', fontSize: '15px' }}>
                          <span>Subtotal:</span>
                          <span>{formatCurrency(calculateSubtotal())}</span>
                        </div>
                        {Object.entries(getVatByRateFromBill()).map(([rate, amt]) => (
                          <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#475569', fontWeight: '600' }}>
                            <span>VAT {rate}%:</span>
                            <span>{formatCurrency(amt)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#475569', fontWeight: '700', fontSize: '15px' }}>
                          <span>Total VAT:</span>
                          <span>{formatCurrency(calculateVAT())}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '20px', borderTop: '2px dashed #cbd5e1', color: '#ef4444', fontWeight: '800', fontSize: '18px' }}>
                          <span>Grand Total:</span>
                          <span className="grand-total">{formatCurrency(calculateGrandTotal())}</span>
                        </div>
                      </div>
                      {/* Receipt Preview Section - Hidden in UI, shown only in print */}
                      {renderReceiptPreview()}
                      <form onSubmit={handleCardSubmit}>
                        <div className="form-group mt-4">
                          <label htmlFor="transactionNumber">
                            Enter Reference / Order Number <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            id="transactionNumber"
                            ref={inputRef}
                            style={{ borderColor: errors.transactionNumber ? '#ef4444' : '' }}
                            className="form-control"
                            placeholder="Enter Reference / Order Number"
                            value={transactionNumber}
                            onChange={handleInputChange}
                            maxLength="50"
                            disabled={isLoading}
                            required
                          />
                          {errors.transactionNumber && (
                            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>{errors.transactionNumber}</div>
                          )}
                          {cardData && (
                            <div style={{ color: '#10b981', fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>
                              Order ID: **** **** **** {cardData.cardNumber?.slice(-4)} (Expiry: {cardData.expiry?.slice(0, 2)}/{cardData.expiry?.slice(2)})
                            </div>
                          )}
                        </div>

                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="cancel-btn"
                            onClick={simulateCardSwipe}
                            disabled={isLoading}
                            style={{ width: '100%' }}
                          >
                            Simulate Scanner (Test)
                          </button>
                        </div>
                        <div className="text-center mt-3" style={{ display: 'flex', gap: '16px' }}>
                          <button
                            type="submit"
                            className="save-btn" style={{ flex: 1, padding: '16px', fontSize: '16px', fontWeight: '600', position: 'relative' }}
                            disabled={isLoading}
                          >
                            <i className="fas fa-check-circle me-2"></i>
                            {isLoading ? "Processing..." : "Confirm Card Payment"} {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8em', padding: '4px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}>F4</span>}
                          </button>
                          <button
                            type="button"
                            className="cancel-btn" style={{ flex: 1, padding: '16px', fontSize: '16px', fontWeight: '600', position: 'relative', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px' }}
                            onClick={handleEdit}
                            disabled={isLoading}
                          >
                            <i className="fas fa-edit me-2"></i> Edit Order
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="cash-empty">
                      <p>No active bill details found.</p>
                      <button
                        className="cancel-btn" style={{ width: '100%', position: 'relative' }}
                        onClick={handleBack}
                        disabled={isLoading}
                      >
                        Return to Main {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8em', padding: '4px 8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>F2</span>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* UPDATED: Modal for bill details and actions - with VAT breakdown */}
          <Modal show={showModal} onHide={handleCancel} size="lg" centered>
            <Modal.Header closeButton className="cash-modal-header">
              <Modal.Title>Bill Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <label className="form-label">Email Receipt To:</label>
                <input
                  type="email"
                  className="form-control cash-modal-input"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
              </div>
              {billDetails && (
                <div>
                  <table className="table table-striped table-bordered">
                    <tbody>
                      <tr>
                        <td style={{ width: "50%", textAlign: "left" }}>
                          <strong>Invoice No:</strong>
                        </td>
                        <td style={{ width: "50%", textAlign: "right", whiteSpace: "nowrap" }}>{billDetails.invoice_no}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Customer:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{billDetails.customerName}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Phone:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{billDetails.phoneNumber}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Email:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{billDetails.email}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>WhatsApp:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{billDetails.whatsappNumber}</td>
                      </tr>
                      {billDetails.tableNumber && billDetails.tableNumber !== "N/A" && (
                        <tr>
                          <td style={{ textAlign: "left" }}>
                            <strong>Table:</strong>
                          </td>
                          <td style={{ textAlign: "right" }}>{billDetails.tableNumber}</td>
                        </tr>
                      )}
                      {hasDeliveryAddress && formattedDeliveryAddress && (
                        <tr>
                          <td style={{ textAlign: "left" }}>
                            <strong>Delivery Address:</strong>
                          </td>
                          <td style={{ textAlign: "right" }}>{formattedDeliveryAddress}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Payment Mode:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{billDetails.payments?.[0]?.mode_of_payment || location.state?.method || "AGGREGATOR"}</td>
                      </tr>
                      {cardData && (
                        <tr>
                          <td style={{ textAlign: "left" }}>
                            <strong>Card Number:</strong>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            **** **** **** {cardData.cardNumber?.slice(-4)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Transaction Number:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{transactionNumber}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Date:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{getFormattedDate(currentTime, settings.dateFormat)}</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left" }}>
                          <strong>Time:</strong>
                        </td>
                        <td style={{ textAlign: "right" }}>{getFormattedTime(currentTime, settings.timeFormat)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <h5>Items:</h5>
                  <div className="table-responsive">
                    <table
                      className="table table-striped table-bordered"
                      style={{ fontSize: "13px", color: "black", fontWeight: "bold" }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}>T.No.</th>
                          <th>Item Details</th>
                          <th style={{ width: "80px" }}>Qty</th>
                          <th style={{ width: "80px" }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billDetails.items.map((item, index) => {
                          const { basePrice, icePrice, spicyPrice } = calculateItemPrices(item);
                          return (
                            <React.Fragment key={index}>
                              <tr>
                                <td>{billDetails.tableNumber}</td>
                                <td>
                                  <strong>{getItemDisplayName(item)}</strong>
                                </td>
                                <td>{item.quantity}</td>
                                <td>
                                  {item.originalBasePrice ? (
                                    <>
                                      <span className="strikethroughStyle">{formatCurrency(item.originalBasePrice)}</span> {formatCurrency(basePrice)}
                                    </>
                                  ) : (
                                    formatCurrency(basePrice)
                                  )}
                                </td>
                              </tr>
                              {item.isCombo && item.comboItems && item.comboItems.map((comboItem, cIndex) => (
                                <tr key={`${index}-combo-${cIndex}`}>
                                  <td></td>
                                  <td>
                                    <div style={{ fontSize: "12px" }}>+ {comboItem.name}</div>
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td>{formatCurrency(comboItem.price)}</td>
                                </tr>
                              ))}
                              {item.icePreference === "with_ice" && icePrice > 0 && (
                                <tr>
                                  <td></td>
                                  <td>
                                    <div style={{ fontSize: "12px" }}>+ Ice ({formatCurrency(icePrice)})</div>
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td>{formatCurrency(icePrice)}</td>
                                </tr>
                              )}
                              {item.isSpicy && spicyPrice > 0 && (
                                <tr>
                                  <td></td>
                                  <td>
                                    <div style={{ fontSize: "12px" }}>+ Spicy ({formatCurrency(spicyPrice)})</div>
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td>{formatCurrency(spicyPrice)}</td>
                                </tr>
                              )}
                              {item.customVariantsDetails &&
                                Object.keys(item.customVariantsDetails).length > 0 &&
                                Object.entries(item.customVariantsDetails).map(([variantName, variant], idx) => (
                                  <tr key={`${index}-custom-${idx}`}>
                                    <td></td>
                                    <td>
                                      <div style={{ color: "#888", fontSize: "12px" }}>
                                        + {variant.heading}: {variant.name} ({formatCurrency(variant.price)})
                                      </div>
                                    </td>
                                    <td>{item.customVariantsQuantities?.[variantName] || 1}</td>
                                    <td>{formatCurrency(variant.price)}</td>
                                  </tr>
                                ))}
                              {item.itemToppings &&
                                Object.keys(item.itemToppings).length > 0 &&
                                Object.entries(item.itemToppings).map(([name, top], idx) => (
                                  <tr key={`${index}-topping-${idx}`}>
                                    <td></td>
                                    <td>
                                      <div style={{ color: "#888", fontSize: "12px" }}>
                                        + {top.name} (Topping)
                                      </div>
                                    </td>
                                    <td>{item.quantity * (top.qty || 1)}</td>
                                    <td>{formatCurrency(top.price)}</td>
                                  </tr>
                                ))}
                              {item.addons &&
                                item.addons.map(
                                  (addon, idx) =>
                                    addon.addon_quantity > 0 && (
                                      <React.Fragment key={`${index}-addon-${idx}`}>
                                        <tr>
                                          <td></td>
                                          <td>
                                            <div style={{ color: "#2ecc71", fontSize: "12px" }}>
                                              + Addon: {addon.addon_name}{addon.size ? ` (${addon.size})` : ""}
                                            </div>
                                          </td>
                                          <td>{addon.addon_quantity}</td>
                                          <td>{formatCurrency(addon.addon_price)}</td>
                                        </tr>
                                        {addon.isSpicy && addon.spicyPrice > 0 && (
                                          <tr>
                                            <td></td>
                                            <td>
                                              <div style={{ color: "#888", fontSize: "12px" }}>
                                                + Spicy ({formatCurrency(addon.spicyPrice)})
                                              </div>
                                            </td>
                                            <td>{addon.addon_quantity}</td>
                                            <td>{formatCurrency(addon.spicyPrice * addon.addon_quantity)}</td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    )
                                )}
                              {item.combos &&
                                item.combos.map(
                                  (combo, idx) =>
                                    combo.combo_quantity > 0 && (
                                      <React.Fragment key={`${index}-combo-${idx}`}>
                                        <tr>
                                          <td></td>
                                          <td>
                                            <div style={{ color: "#e74c3c", fontSize: "12px" }}>
                                              + Combo: {combo.name1}{combo.size ? ` (${combo.size})` : ""}
                                            </div>
                                          </td>
                                          <td>{combo.combo_quantity}</td>
                                          <td>{formatCurrency(combo.combo_price)}</td>
                                        </tr>
                                        {combo.isSpicy && combo.spicyPrice > 0 && (
                                          <tr>
                                            <td></td>
                                            <td>
                                              <div style={{ color: "#888", fontSize: "12px" }}>
                                                + Spicy ({formatCurrency(combo.spicyPrice)})
                                              </div>
                                            </td>
                                            <td>{combo.combo_quantity}</td>
                                            <td>{formatCurrency(combo.spicyPrice * combo.combo_quantity)}</td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    )
                                )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* UPDATED: VAT breakdown in modal */}
                  <div className="mt-3">
                    <p>
                      <strong>Subtotal:</strong> {formatCurrency(calculateSubtotal())}
                    </p>
                    {Object.entries(getVatByRateFromBill()).map(([rate, amt]) => (
                      <p key={rate}>
                        <strong>VAT {rate}%:</strong> {formatCurrency(amt)}
                      </p>
                    ))}
                    <p>
                      <strong>Total VAT:</strong> {formatCurrency(calculateVAT())}
                    </p>
                    <p>
                      <strong>Grand Total:</strong> {formatCurrency(calculateGrandTotal())}
                    </p>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="cash-modal-footer">
              <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
                Close
              </Button>
              <Button variant="info" onClick={handleEmail} disabled={isLoading}>
                {isLoading ? "Sending..." : "Send Email"} {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ marginLeft: '4px', fontSize: '0.8em', padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}>F8</span>}
              </Button>
              <Button variant="primary" onClick={handlePrint} disabled={isLoading}>
                {isLoading ? "Processing..." : "Print Preview"} {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ marginLeft: '4px', fontSize: '0.8em', padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}>Enter</span>}
              </Button>
              <Button variant="danger" onClick={handleModalClose} disabled={isLoading}>
                Close &amp; Exit
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
}
export default Aggregator;
